"use client";

import { useMemo, useState } from "react";
import { Card, CardContent } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { AIAssistantModal } from "@/components/ui/AIAssistantModal";

type AppointmentType = "PRIVATE_SHOWING" | "CONSULTATION" | "FITTING" | "PICKUP" | "DROP_OFF";

interface Appointment {
  id: string;
  type: AppointmentType;
  customerName: string;
  customerInitials: string;
  isVip: boolean;
  startsAt: string;
  durationMinutes: number;
  salesperson: string;
  notes: string;
}

const TYPE_META: Record<AppointmentType, { label: string; color: string }> = {
  PRIVATE_SHOWING: { label: "Private showing", color: "border-amber-200/30 bg-amber-200/10 text-amber-200" },
  CONSULTATION: { label: "Consultation", color: "border-violet-300/30 bg-violet-300/10 text-violet-200" },
  FITTING: { label: "Fitting", color: "border-sky-300/30 bg-sky-300/10 text-sky-200" },
  PICKUP: { label: "Pickup", color: "border-emerald-300/30 bg-emerald-300/10 text-emerald-200" },
  DROP_OFF: { label: "Drop-off", color: "border-orange-300/30 bg-orange-300/10 text-orange-200" },
};

function todayAt(hour: number, minute = 0): Date {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d;
}

const DEMO_APPOINTMENTS: Appointment[] = [
  {
    id: "apt-001",
    type: "PRIVATE_SHOWING",
    customerName: "Michael Chen",
    customerInitials: "MC",
    isVip: true,
    startsAt: todayAt(11, 0).toISOString(),
    durationMinutes: 60,
    salesperson: "Sophia Lin",
    notes: "Reviewing anniversary pieces — 2ct+ cushion cuts, platinum",
  },
  {
    id: "apt-002",
    type: "FITTING",
    customerName: "Emma Chen",
    customerInitials: "EC",
    isVip: false,
    startsAt: todayAt(13, 30).toISOString(),
    durationMinutes: 30,
    salesperson: "James Ruiz",
    notes: "Resize confirmation — size 6.5",
  },
  {
    id: "apt-003",
    type: "PICKUP",
    customerName: "Sofia Rodriguez",
    customerInitials: "SR",
    isVip: true,
    startsAt: todayAt(15, 0).toISOString(),
    durationMinutes: 15,
    salesperson: "Sophia Lin",
    notes: "Datejust pickup — REP-2026-0002",
  },
  {
    id: "apt-004",
    type: "CONSULTATION",
    customerName: "Priya Patel",
    customerInitials: "PP",
    isVip: true,
    startsAt: todayAt(16, 30).toISOString(),
    durationMinutes: 45,
    salesperson: "Marcus Bell",
    notes: "Custom pendant design — concept review",
  },
];

function formatTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

export default function AppointmentsPage() {
  const [appointments] = useState<Appointment[]>(DEMO_APPOINTMENTS);
  const [showNewModal, setShowNewModal] = useState(false);

  const stats = useMemo(() => {
    const today = appointments.length;
    const vip = appointments.filter((a) => a.isVip).length;
    const showings = appointments.filter((a) => a.type === "PRIVATE_SHOWING").length;
    return { today, vip, showings };
  }, [appointments]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-sky-300/30 to-sky-500/10">
              <svg className="h-4 w-4 text-sky-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h1 className="text-2xl font-semibold text-white">Appointments</h1>
          </div>
          <p className="mt-1 text-sm text-muted">
            Private showings, fittings, consultations, and pickups on the calendar.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowNewModal(true)}
          className="rounded-xl bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-white/90"
        >
          + New appointment
        </button>
      </div>

      <AIAssistantModal
        isOpen={showNewModal}
        onClose={() => setShowNewModal(false)}
        entity="appointment"
        example="Book Emma Torres for a private showing Thursday at 2pm with Olivia."
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Today</p>
          <p className="mt-1 text-xl font-semibold text-white">{stats.today}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">VIP clients</p>
          <p className="mt-1 text-xl font-semibold text-amber-200">{stats.vip}</p>
        </CardContent></Card>
        <Card><CardContent className="py-4">
          <p className="text-xs uppercase tracking-wider text-text-dim">Private showings</p>
          <p className="mt-1 text-xl font-semibold text-white">{stats.showings}</p>
        </CardContent></Card>
      </div>

      {/* Today timeline */}
      <Card>
        <CardContent className="py-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white">Today</h2>
            <span className="text-xs text-text-dim">
              {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
            </span>
          </div>
          <div className="space-y-3">
            {appointments.map((a) => {
              const meta = TYPE_META[a.type];
              return (
                <div
                  key={a.id}
                  className="flex gap-4 rounded-xl border border-white/5 bg-white/5 p-4 transition hover:border-white/10"
                >
                  <div className="flex w-20 flex-shrink-0 flex-col items-start">
                    <span className="text-base font-semibold text-white">{formatTime(a.startsAt)}</span>
                    <span className="text-[10px] uppercase tracking-wider text-text-dim">{a.durationMinutes}min</span>
                  </div>
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider ${meta.color}`}>
                        {meta.label}
                      </span>
                      {a.isVip && <Badge variant="warning" size="sm">VIP</Badge>}
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-amber-200 to-amber-400 text-[10px] font-semibold text-black">
                        {a.customerInitials}
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-white">{a.customerName}</p>
                        <p className="text-[11px] text-text-dim">with {a.salesperson}</p>
                      </div>
                    </div>
                    <p className="text-xs text-white/60">{a.notes}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
